import {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DragStart,
  DropResult,
} from '@hello-pangea/dnd';
import isNil from 'lodash/isNil';
import {
  useNotify,
} from 'core/hooks';
import {
  useAssociateInsuranceCard,
  useDisassociateInsuranceCard,
} from 'views/EMR/components/Insurance/hooks';
import {
  BusyContainerState,
  getInsuranceCardMoveKind,
  getInsuranceCardRefetchPlan,
  InsuranceCardDragItem,
  parseDroppableId,
} from 'views/EMR/components/Insurance/insuranceCardDnD';

type RefetchFn = () => Promise<unknown>;

type Params = {
  patientId: number;
};

export default function useInsuranceCardDnD({ patientId }: Params) {
  const notify = useNotify();
  const [busyState, setBusyState] = useState<BusyContainerState | null>(null);
  const [isDraggingInsuranceCard, setIsDraggingInsuranceCard] = useState(false);
  const [activeDragSourceKey, setActiveDragSourceKey] = useState<string | null>(null);
  const insuranceRefetchRegistryRef = useRef<Record<number, RefetchFn>>({});
  const unassociatedRefetchRef = useRef<RefetchFn | null>(null);
  const dragItemsByIdRef = useRef<Record<string, InsuranceCardDragItem>>({});
  const dragItemIdsByContainerRef = useRef<Record<string, string[]>>({});
  const {
    mutateAsync: associateInsuranceCard,
  } = useAssociateInsuranceCard();
  const {
    mutateAsync: disassociateInsuranceCard,
  } = useDisassociateInsuranceCard();

  const busyContainerKeys = useMemo(() => {
    const containerKeys: string[] = [];

    if (!isNil(busyState)) {
      containerKeys.push(busyState.sourceKey, busyState.destinationKey);
    }

    return containerKeys;
  }, [busyState]);

  const registerInsuranceRefetch = useCallback((insuranceId: number, refetch: RefetchFn) => {
    insuranceRefetchRegistryRef.current[insuranceId] = refetch;
  }, []);

  const unregisterInsuranceRefetch = useCallback((insuranceId: number) => {
    delete insuranceRefetchRegistryRef.current[insuranceId];
  }, []);

  const registerUnassociatedRefetch = useCallback((refetch: RefetchFn) => {
    unassociatedRefetchRef.current = refetch;
  }, []);

  const unregisterUnassociatedRefetch = useCallback(() => {
    unassociatedRefetchRef.current = null;
  }, []);

  const registerContainerItems = useCallback((
    containerKey: string,
    items: InsuranceCardDragItem[],
  ) => {
    const previousItemIds = dragItemIdsByContainerRef.current[containerKey] ?? [];

    previousItemIds.forEach((itemId) => {
      delete dragItemsByIdRef.current[itemId];
    });

    dragItemIdsByContainerRef.current[containerKey] = items.map((item) => item.dragId);
    items.forEach((item) => {
      dragItemsByIdRef.current[item.dragId] = item;
    });
  }, []);

  const unregisterContainerItems = useCallback((containerKey: string) => {
    const itemIds = dragItemIdsByContainerRef.current[containerKey] ?? [];

    itemIds.forEach((itemId) => {
      delete dragItemsByIdRef.current[itemId];
    });

    delete dragItemIdsByContainerRef.current[containerKey];
  }, []);

  const onDragStart = useCallback((start: DragStart) => {
    const isInsuranceCardDrag = start.type === 'insurance-card';

    setIsDraggingInsuranceCard(isInsuranceCardDrag);
    setActiveDragSourceKey(isInsuranceCardDrag ? start.source.droppableId : null);
  }, []);

  const refetchAffectedContainers = useCallback(async (
    item: InsuranceCardDragItem,
    destinationDroppableId: string,
    shouldRefetchUnassociatedOverride: boolean,
  ) => {
    const destination = parseDroppableId(destinationDroppableId);
    const refetchPlan = getInsuranceCardRefetchPlan(item, destination, {
      shouldRefetchUnassociated: shouldRefetchUnassociatedOverride,
    });
    const refetchTasks = refetchPlan.insuranceIds
      .map((insuranceId) => insuranceRefetchRegistryRef.current[insuranceId])
      .filter((refetch): refetch is RefetchFn => !isNil(refetch))
      .map((refetch) => refetch());

    if (refetchPlan.shouldRefetchUnassociated && !isNil(unassociatedRefetchRef.current)) {
      refetchTasks.push(unassociatedRefetchRef.current());
    }

    await Promise.all(refetchTasks);
  }, []);

  const onDragEnd = useCallback(async (result: DropResult) => {
    setIsDraggingInsuranceCard(false);
    setActiveDragSourceKey(null);

    const { destination, draggableId, source } = result;
    const sourceKey = source.droppableId;
    const destinationKey = destination?.droppableId ?? '';
    const isSameContainer = sourceKey === destinationKey;
    const item = dragItemsByIdRef.current[draggableId];
    const hasDestination = !isNil(destination);
    const isMatchingPatient = (item?.patientId ?? 0) === patientId;
    let shouldRefetchUnassociatedOverride = false;
    let didDisassociateBeforeFailure = false;

    if (hasDestination && !isNil(item) && isMatchingPatient && !isSameContainer) {
      const target = parseDroppableId(destinationKey);
      const moveKind = getInsuranceCardMoveKind(item, target);

      setBusyState({
        sourceKey,
        destinationKey,
      });

      try {
        if (moveKind === 'insurance-to-insurance') {
          await disassociateInsuranceCard(item.insuranceAttachmentId ?? 0);
          didDisassociateBeforeFailure = true;
          await associateInsuranceCard({
            insuranceId: target.type === 'insurance' ? target.insuranceId : 0,
            patientMultiMediaId: item.mediaId,
          });
        } else if (moveKind === 'insurance-to-unassociated') {
          await disassociateInsuranceCard(item.insuranceAttachmentId ?? 0);
        } else if (moveKind === 'unassociated-to-insurance') {
          await associateInsuranceCard({
            insuranceId: target.type === 'insurance' ? target.insuranceId : 0,
            patientMultiMediaId: item.mediaId,
          });
        }
      } catch (error) {
        shouldRefetchUnassociatedOverride = didDisassociateBeforeFailure
          && moveKind === 'insurance-to-insurance';
        notify({
          variant: 'error',
          message: shouldRefetchUnassociatedOverride
            ? 'Card was removed from the source insurance but could not be associated to the destination.'
            : 'Unable to move insurance card. Please try again.',
        });
        console.error(error);
      } finally {
        await refetchAffectedContainers(
          item,
          destinationKey,
          shouldRefetchUnassociatedOverride,
        );
        setBusyState(null);
      }
    }
  }, [
    associateInsuranceCard,
    disassociateInsuranceCard,
    notify,
    patientId,
    refetchAffectedContainers,
  ]);

  return {
    activeDragSourceKey,
    busyContainerKeys,
    isDraggingInsuranceCard,
    onDragEnd,
    onDragStart,
    registerContainerItems,
    registerInsuranceRefetch,
    registerUnassociatedRefetch,
    unregisterContainerItems,
    unregisterInsuranceRefetch,
    unregisterUnassociatedRefetch,
  };
}
